import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import type { FeedbackParams } from "@memorang/features/src/feedback/types";
import Constants from "expo-constants";
import { usePathname } from "expo-router";
import { useMemo } from "react";
import { Platform } from "react-native";

type UseFeedbackParamsOptions = {
	view?: string;
	type?: "Review" | "Content Flagged";
	itemId?: string;
	sessionId?: string;
	threadId?: string;
	runId?: string;
	featureName?: string;
	sessionType?: string;
	rating?: string;
	itemVersionId?: string;
};

type ExtendedFeedbackParams = FeedbackParams & {
	aiThreadId?: string;
	aiRunId?: string;
	featureName?: string;
};

export const useFeedbackParams = (
	options: UseFeedbackParamsOptions = {},
): ExtendedFeedbackParams => {
	const pathname = usePathname();
	const { newUserInfo } = useAuthContext();
	const companyName = useAppStore((store) => store.companyName);
	const appName = useAppStore((store) => store.app.name);
	const {
		currentExamContext: { projectId, organizationId, examName },
	} = useExamContext();

	const {
		view,
		type,
		itemId,
		sessionId,
		threadId,
		runId,
		rating,
		featureName,
		sessionType,
	} = options;

	return useMemo(
		() => ({
			email: newUserInfo?.email ?? "",
			userName: newUserInfo?.name ?? "",
			appName,
			platform: Platform.OS,
			appLanguage: i18n.language,
			currentAppVersion: Constants.expoConfig?.version,
			view: view ?? pathname,
			type: (type as "Review" | "Content Flagged") ?? ("Review" as const),
			companyName,
			bundleName: examName,
			projectId,
			organizationId,
			...(itemId && {
				itemId,
				itemVersionId: "1",
				sessionType: sessionType ?? "FORMATIVE",
			}),
			...(sessionId && { sessionId }),
			...(threadId && { threadId }),
			...(runId && { runId }),
			...(rating && {
				rating: rating as "positive" | "negative",
			}),
			...(featureName && { featureName }),
		}),
		[
			newUserInfo?.email,
			newUserInfo?.name,
			appName,
			pathname,
			companyName,
			examName,
			projectId,
			organizationId,
			view,
			type,
			itemId,
			sessionId,
			threadId,
			runId,
			rating,
			featureName,
			sessionType,
		],
	);
};
